import { z } from 'zod';
import { digestSchema } from '../hashing/hash.js';
import { fileCategorySchema, fileContainerSchema, fileStoreSchema, resolveFileStore, } from './categories.js';
import { fileIdSchema } from './stored-file.js';
/**
 * Where an approved file will be committed, decided AT APPROVAL and carried on the `store-file`
 * effect (#183, ADR-009 §2).
 *
 * Resolved at enqueue for the reason #61 gave for RoutePlan: a category change between approval
 * and delivery must not re-home a file HR already approved. It carries ids and a digest only —
 * no name, no file name — because an envelope is a Cosmos document AND a queue message.
 *
 * `store` and `container` are DERIVED from `category` by `storePlanFor`, and a plan whose pair
 * disagrees with the category does not parse. A segregated category therefore cannot arrive at
 * the handler addressed to the records account, whoever wrote the envelope.
 */
export const storePlanSchema = z
    .object({
    /** Deterministic per upload, so a redelivery and a re-approval meet the same FileRecord. */
    fileId: fileIdSchema,
    category: fileCategorySchema,
    store: fileStoreSchema,
    container: fileContainerSchema,
    /** The digest the upload was accepted and scanned under. The committed bytes must re-hash to it. */
    sha256: digestSchema,
})
    .strict()
    .superRefine((plan, ctx) => {
    const resolved = resolveFileStore(plan.category);
    if (plan.store !== resolved.store || plan.container !== resolved.container) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['container'], message: `${plan.category} is committed to ${resolved.store}/${resolved.container}` });
    }
});
/** The one way a plan is built: the location comes from the category, never from the caller. */
export function storePlanFor(input) {
    return storePlanSchema.parse({ fileId: input.fileId, category: input.category, ...resolveFileStore(input.category), sha256: input.sha256 });
}
/**
 * Why a commit waits or will not happen, as a CLOSED code (#183, reusing #171's once-per-fact
 * design). The code — never a reason sentence — is what a `file.store-parked` or
 * `file.store-blocked` audit row carries: a sentence may one day quote a file name, a code cannot.
 */
export const fileStoreParkedCodeSchema = z.enum([
    /** An envelope with no plan: enqueued before plans existed, or not by the approval path. */
    'no-plan',
    /** RECORDS_BLOB_ENDPOINT / RESTRICTED_BLOB_ENDPOINT are not set (#182). */
    'store-not-configured',
    'upload-not-found',
    'scan-pending',
    'scan-unavailable',
    'scan-no-verdict',
    'bytes-unreadable',
    /**
     * A `correct-file` envelope whose correction document is not there yet (#194). The request
     * writes the envelope first, so a request that died in between leaves this until it is retried.
     */
    'correction-not-found',
]);
export const fileStoreBlockedCodeSchema = z.enum([
    'upload-instance-mismatch',
    'not-accepted',
    'scan-infected',
    'scan-failed',
    'plan-hash-mismatch',
    'bytes-changed',
    'content-type-not-allowed',
    /** The record store refused the request before sending it (#182 `RecordRefused`). */
    'store-refused',
    /** Different bytes already sit at the content address (#182 `RecordConflict`). */
    'name-occupied',
    /** The committed version did not read back as the bytes written (#182 `RecordIntegrityError`). */
    'integrity-failed',
    /** A FileRecord with this id already describes a different file. */
    'record-conflict',
    /** The plan is for a different category than the effect names — a forged or corrupted envelope. */
    'plan-category-mismatch',
    /** A `correct-file` envelope that disagrees with the correction it names (#194). */
    'correction-mismatch',
    /** A `correct-file` envelope whose misfiled FileRecord does not exist (#194). Append-only, so it never will. */
    'misfiled-not-found',
]);
export const fileStoreCodeSchema = z.enum([...fileStoreParkedCodeSchema.options, ...fileStoreBlockedCodeSchema.options]);
//# sourceMappingURL=store-plan.js.map